import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { UserVideoType } from ".";
import {
    useChannelRoomStore,
    useChannelStore,
    useGlobalStore,
    VideoWindowLayoutTypes,
} from "@obsbot/remo-stores";
import { eventBus } from "@obsbot/remo-utils";
import { ArrowIconPNG } from "../SvgIconCom";
import { VideoWindowItem } from "./VideoWindowItem";
import { useTranslate } from "@obsbot/remo-i18n";
import InviteFriendIconSVG from "./inviteIcon.svg";
import CustomizeChannelIconSVG from "./customizeChannel.svg";
import { REMO_GAME_WEB_URL } from "@obsbot/remo-server-api";

export interface VideoWindowProps {
    members: UserVideoType[];
    channelLayoutType: VideoWindowLayoutTypes;
    mainContent: UserVideoType | null;
    genClassName: (member: UserVideoType) => string;
    setMainContent: (member: UserVideoType) => void;
    setRefs: (
        userID: string,
        videoRef: HTMLDivElement,
    ) => (refs: { divRef: HTMLDivElement; videoRef: HTMLDivElement }) => void;
}

export const VideoWindow: React.FC<VideoWindowProps> = ({
    channelLayoutType,
    members,
    mainContent,
    genClassName,
    setMainContent,
    setRefs,
}): ReactElement => {
    const t = useTranslate();
    const { needShowGuide } = useGlobalStore();
    const { ownerName, channelName } = useChannelRoomStore();
    const { activeChannel } = useChannelStore();
    // 判断是否超出滚动条
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // 存储视频
    const videoDomMap = useRef<Map<string, HTMLDivElement>>(new Map());
    // 均分的布局分页
    const [page, setPage] = useState<number>(0);
    // 上下列表的显示范围
    const showRange = useRef<[number, number]>([0, 2]);
    const handleScroll = useCallback((otherVideoWindow: UserVideoType[], isLeft = false): void => {
        const scrollContainer = scrollContainerRef.current;
        const primeRange = showRange.current;
        const [startIdx, endIdx] = primeRange;
        if (isLeft) {
            showRange.current = [
                Math.max(startIdx - 1, 0),
                Math.min(endIdx - 1, otherVideoWindow.length - 1),
            ];
        } else {
            showRange.current = [
                Math.max(startIdx + 1, 0),
                Math.min(endIdx + 1, otherVideoWindow.length - 1),
            ];
        }
        if (scrollContainer) {
            const scrollDistance = window.innerHeight * 0.35 + 14;
            scrollContainer.scrollBy({
                left: isLeft ? -scrollDistance : scrollDistance,
                behavior: "smooth",
            });
        }
    }, []);

    useEffect(() => {
        const handleUserLeave = (userID: string): void => {
            videoDomMap.current.delete(userID);
        };
        eventBus.on("user-leave-channel", handleUserLeave);
        return () => {
            eventBus.off("user-leave-channel", handleUserLeave);
        };
    }, []);

    const getVideoDom = useCallback((userUUID: string) => {
        if (!videoDomMap.current.has(userUUID)) {
            console.log("[Video] [LOG] videoDom无缓存, 创建元素", userUUID);
            const div = document.createElement("div");
            div.className = "video-window-content";
            videoDomMap.current.set(userUUID, div);
        }
        return videoDomMap.current.get(userUUID)!;
    }, []);

    const handleMemberCameraOff = useCallback((member: UserVideoType) => {
        videoDomMap.current.delete(member.user.userUUID);
    }, []);

    useEffect(() => {
        const handleSwitchChannel = (): void => {
            videoDomMap.current.clear();
        };
        eventBus.on("switch-channel", handleSwitchChannel);
        return () => {
            eventBus.off("switch-channel", handleSwitchChannel);
            handleSwitchChannel();
        };
    }, []);

    if ((needShowGuide.channel || needShowGuide.specialEffect) && members.length === 1) {
        const member = members[0];
        if (!member) {
            return <></>;
        }
        const handleChannelInvite = (): void => {
            if (!activeChannel) {
                return;
            }
            eventBus.emit("guide-action", {
                action: "invite",
                data: {
                    url: `${REMO_GAME_WEB_URL}#/channel/invite/${activeChannel.inviteFlag}`,
                },
            });
        };
        const handleChannelSetting = (): void => {
            if (!activeChannel) {
                return;
            }
            eventBus.emit("guide-action", {
                action: "customize",
                data: {
                    channelUuid: activeChannel.channelUuid,
                },
            });
        };
        return (
            <div className="only-one-video-container">
                <div className="channel-description">
                    {t("welcome-to-channel", {
                        user: ownerName,
                        channel: channelName,
                    })}
                </div>
                <div className="video-container">
                    <VideoWindowItem
                        key={member.user.userUUID}
                        ref={setRefs(member.user.userUUID, getVideoDom(member.user.userUUID))}
                        className={genClassName(member)}
                        handleMemberCameraOff={() => handleMemberCameraOff(member)}
                        member={member}
                        videoContainer={getVideoDom(member.user.userUUID)}
                    />
                </div>
                <div className="action-buttons">
                    <div className="action-button" onClick={handleChannelInvite}>
                        <div className="icon">
                            <img src={InviteFriendIconSVG} />
                        </div>
                        <div className="text">{t("invite-your-friends")}</div>
                        <div className="arrow">
                            <ArrowIcon />
                        </div>
                    </div>
                    <div className="action-button" onClick={handleChannelSetting}>
                        <div className="icon">
                            <img src={CustomizeChannelIconSVG} />
                        </div>
                        <div className="text">{t("customize-channel")}</div>
                        <div className="arrow">
                            <ArrowIcon />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (members.length === 1) {
        const member = members[0];
        return (
            <VideoWindowItem
                key={member.user.userUUID}
                ref={setRefs(member.user.userUUID, getVideoDom(member.user.userUUID))}
                className={genClassName(member)}
                handleMemberCameraOff={() => handleMemberCameraOff(member)}
                member={member}
                videoContainer={getVideoDom(member.user.userUUID)}
            />
        );
    }
    switch (channelLayoutType) {
        case VideoWindowLayoutTypes.Equal: {
            const pageSize = 6;
            const curPageMembers = members.slice(page * pageSize, page * pageSize + pageSize);
            const isShowLastPage = page !== 0;
            const isShowNextPage = members.length > page * pageSize + pageSize;
            return (
                <>
                    {curPageMembers.map(member => {
                        return (
                            <VideoWindowItem
                                key={member.user.userUUID}
                                ref={setRefs(
                                    member.user.userUUID,
                                    getVideoDom(member.user.userUUID),
                                )}
                                className={genClassName(member)}
                                handleMemberCameraOff={() => handleMemberCameraOff(member)}
                                member={member}
                                videoContainer={getVideoDom(member.user.userUUID)}
                            />
                        );
                    })}
                    {isShowLastPage && (
                        <div
                            className="video-window-next left"
                            onClick={() => setPage(page => page - 1)}
                        >
                            <img src={ArrowIconPNG} />
                        </div>
                    )}
                    {isShowNextPage && (
                        <div
                            className="video-window-next right"
                            onClick={() => setPage(page => page + 1)}
                        >
                            <img src={ArrowIconPNG} />
                        </div>
                    )}
                </>
            );
        }
        case VideoWindowLayoutTypes.Bottom:
        case VideoWindowLayoutTypes.Top: {
            const mainVideoWindowIdx = members.findIndex(
                member => member.user.userUUID === mainContent?.user.userUUID,
            );
            const mainVideoWindow =
                mainVideoWindowIdx === -1 ? members[0] : members[mainVideoWindowIdx];
            const otherVideoWindow = members.filter(
                member => member.user.userUUID !== mainVideoWindow.user.userUUID,
            );
            if (!mainVideoWindow) {
                return <></>;
            }
            const hasScrollbar = scrollContainerRef.current
                ? scrollContainerRef.current?.scrollWidth > scrollContainerRef.current?.clientWidth
                : false;

            const topVal = channelLayoutType === VideoWindowLayoutTypes.Bottom ? "87%" : "11%";

            return (
                <>
                    <div className="video-window-main">
                        <VideoWindowItem
                            key={mainVideoWindow.user.userUUID}
                            ref={setRefs(
                                mainVideoWindow.user.userUUID,
                                getVideoDom(mainVideoWindow.user.userUUID),
                            )}
                            className={genClassName(mainVideoWindow)}
                            handleMemberCameraOff={() => handleMemberCameraOff(mainVideoWindow)}
                            member={mainVideoWindow}
                            videoContainer={getVideoDom(mainVideoWindow.user.userUUID)}
                        />
                    </div>
                    <div ref={scrollContainerRef} className="video-window-scroll-list">
                        {otherVideoWindow.map((member, idx) => {
                            return (
                                <VideoWindowItem
                                    key={member.user.userUUID}
                                    ref={setRefs(
                                        member.user.userUUID,
                                        getVideoDom(member.user.userUUID),
                                    )}
                                    canShowVideo={
                                        idx >= showRange.current[0] && idx <= showRange.current[1]
                                    }
                                    className={genClassName(member)}
                                    handleDoubleClick={() => {
                                        eventBus.emit("channel-layout-type-changed", undefined);
                                        setMainContent(member);
                                    }}
                                    handleMemberCameraOff={() => handleMemberCameraOff(member)}
                                    member={member}
                                    videoContainer={getVideoDom(member.user.userUUID)}
                                />
                            );
                        })}
                        {hasScrollbar && (
                            <div
                                className="video-window-next"
                                style={{ top: topVal }}
                                onClick={() => handleScroll(otherVideoWindow)}
                            >
                                <img src={ArrowIconPNG} />
                            </div>
                        )}
                        {hasScrollbar && (
                            <div
                                className="video-window-next left"
                                style={{ top: topVal }}
                                onClick={() => handleScroll(otherVideoWindow, true)}
                            >
                                <img src={ArrowIconPNG} />
                            </div>
                        )}
                    </div>
                </>
            );
        }
    }
};

const ArrowIcon: React.FC = () => (
    <svg
        fill="none"
        height="15"
        version="1.1"
        viewBox="0 0 15 15"
        width="15"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <defs>
            <clipPath id="master_svg0_462_8618">
                <rect height="15" rx="0" width="15" x="0" y="0" />
            </clipPath>
        </defs>
        <g clipPath="url(#master_svg0_462_8618)">
            <g style={{ opacity: 0.800000011920929 }}>
                <path
                    d="M4.27046281,2.18394113Q4.14014137,2.04493153,4.07007068,1.8677382200000001Q4,1.69054492,4,1.5Q4.00000006,1.4015086,4.01921481,1.3049096900000001Q4.03842956,1.20831078,4.07612062,1.1173166Q4.11381161,1.02632242,4.16853046,0.94442987Q4.22324938,0.86253726,4.29289329,0.79289329Q4.36253726,0.72324938,4.44442981,0.66853052Q4.52632242,0.61381161,4.6173166,0.57612056Q4.70831078,0.5384295,4.80490969,0.51921481Q4.9015086,0.50000006,5,0.5Q5.2073067,0.5,5.39752913,0.58241045Q5.58775157,0.66482091,5.72953719,0.81605887L8.729537,4.0160584L11.046869300000001,6.4878798Q11.6138744,7.0926847,11.587655999999999,7.9172406Q11.5613685,8.7439442,10.954276100000001,9.3105621L8.6823189,11.431056L5.6825937,14.230798L5.68231815,14.231055Q5.5434401,14.360674,5.36670464,14.430336Q5.18996921,14.499998,5,14.5Q4.9015086,14.5,4.80490969,14.480783Q4.70831078,14.461569,4.6173166,14.423878Q4.52632242,14.386187,4.4444298700000004,14.331468Q4.36253726,14.27675,4.29289329,14.207106Q4.22324938,14.137462,4.16853052,14.055569Q4.11381161,13.973677,4.07612056,13.882683Q4.0384295,13.791689,4.01921481,13.69509Q4.00000006,13.598492,4,13.5Q4,13.292114,4.08285272,13.101452Q4.16570544,12.910789,4.31768179,12.768945L4.3179577,12.768687L7.317682700000001,9.9689455L9.585,7.8527813L7.2704628,5.3839409L4.27046275,2.18394119L4.27046281,2.18394113Z"
                    fill="#FFFFFF"
                    fillOpacity="1"
                    fillRule="evenodd"
                />
            </g>
        </g>
    </svg>
);
